/**
 * The sentence to show someone when a request fails.
 *
 * Every error toast used to print whatever the API sent back. Some of that is
 * written for patients ("That code has expired. Request a new one."), but much
 * of it is not: "Already exists in the records", "No user found with id: 12",
 * "Care Plan ID does not Exits", or a raw exception message on a 500. Worse,
 * when the request never reached the server there is no response at all, and
 * reading err.response.data threw inside the error handler.
 *
 * So the server's text is shown only when it is one we have read and know is
 * fit to show, either as written or reworded below. Anything else gets the
 * caller's own message, which names what failed and what to do next. DESIGN_SYSTEM.md
 * §9: say what happened, then how to fix it.
 *
 *   toast.error(errorMessage(err, "We couldn't save your notes. Try again in a moment."));
 */

export const NO_CONNECTION =
  "We couldn't reach Youro. Check your internet connection and try again.";

// Server messages already written for the person reading them, shown as-is.
const WRITTEN_FOR_READERS = new Set([
  "Too many sign-in attempts. Please wait 15 minutes and try again.",
  "Too many questionnaires have been submitted from this network. Please try again later.",
  "We couldn't find that questionnaire. Please start again from the condition page.",
  "We couldn't email your score just now. Please check the address and try again.",
  "Request a new code and try again.",
  "Too many incorrect attempts. Request a new code.",
  "That code has expired. Request a new one.",
  "Enter the code we emailed you.",
  // The server's one answer for any code it refuses, including one for an
  // address with no account (OtpService.CODE_REJECTED).
  "That code isn't right or has expired. Check the email we sent, or request a new code.",
  "You're already on that membership.",
  "You don't have an active membership to change.",
  "A one-time pass isn't a membership, so you can't switch to it.",
  "Your active membership already covers this, so a one-time pass isn't needed.",
  "You already have an active membership. Switch plans instead of buying a second one.",
  // A patient's questionnaire for an appointment (QuestionnaireRules).
  "You can only complete questionnaires for your own appointments.",
  "Open this questionnaire from your upcoming appointment.",
  "This appointment was cancelled, so it doesn't have a questionnaire.",
  "The questionnaire for this appointment has closed.",
  "You've already completed the questionnaire for this appointment.",
  "This questionnaire has changed since you started it. Please start it again from your appointment.",
  // Waitlist (WaitlistService, WaitlistLimiter).
  "Choose the state you live in.",
  "Enter a 5-digit zip code, or leave it blank.",
  "Enter your email address so we can tell you when Youro is available.",
  "Too many requests from this network. Please try again later.",
]);

// Server messages that carry a reason worth passing on, in words people use.
const REWORDED = [
  // Signup from a state Youro does not serve (RegistrationService.register). The
  // web and mobile apps ask first, so this is mostly for older app builds.
  [/^Youro isn't available in your state yet. .*$/i, (m) => m[0]],
  // When a questionnaire opens (QuestionnaireRules): the date is the server's.
  [/^The questionnaire for this appointment opens (on [A-Z][a-z]{2}, [A-Z][a-z]{2} \d{1,2}, a week before your visit|after your visit on [A-Z][a-z]{2}, [A-Z][a-z]{2} \d{1,2})\.$/,
    (m) => m[0]],
  [/^That code is not correct\.?$/i,
    () => "That code isn't right. Check the email we sent and enter the code again."],
  [/^Account already exists with provided Email ID$/i,
    () => "An account already uses that email. Sign in instead, or reset your password if you've forgotten it."],
  
  [/^Invalid promo code$/i,
    () => "That promo code isn't valid. Check the spelling and try again."],
  [/^Promo code is required$/i,
    () => "Enter a promo code, then select Apply."],
  [/^User already has an active subscription$/i,
    () => "You already have an active membership, so this code can't be applied."],
  [/^Your current membership has no price recorded, so it can't be changed\.?$/i,
    () => "Your membership can't be changed online. Contact support and we'll switch it for you."],
  [/^Could not send your message\. Please try again or email us directly\.?$/i,
    () => "We couldn't send your message. Try again, or email us at yourohealth1@gmail.com."],
  [/^Could not send the verification email\. Please try again\.?$/i,
    () => "We couldn't send your verification email. Wait a moment, then request a new code."],
  [/^Already exists in the records$/i,
    () => "That name is already in the list. Use a different name, or edit the existing entry."],
  [/^Failed to delete (.+), as it is referenced elsewhere\.?$/i,
    (m) => `That ${m[1]} is still in use, so it can't be deleted. Remove it from anything that uses it, then try again.`],
  [/^Already prescribed\. Can't change (.+)\.?$/i,
    (m) => `This medicine has already been prescribed, so its ${m[1].replace(/\.$/, "")} can't be changed.`],
  [/^An existing appointment already available for given slot$/i,
    () => "That time overlaps something already on your calendar. Pick a different time."],
  [/^All the slots for given time has been booked/i,
    () => "That time has just been booked. Pick a different time."],
  [/^Refill Request not found$/i,
    () => "We couldn't find that refill request. It may already have been handled — refresh to see the latest."],
];

const serverText = (err) => {
  const body = err?.response?.data;
  if (!body) return "";
  if (typeof body === "string") return body.trim();
  const text = body.errorMessage || body.error || body.message || body.detail || body.description;
  return typeof text === "string" ? text.trim() : "";
};

export const errorMessage = (err, fallback) => {
  // A request that never got an answer: offline, DNS, or the API is down.
  if (err && !err.response && (err.request || err.message === "Network Error")) {
    return NO_CONNECTION;
  }

  const text = serverText(err);
  if (WRITTEN_FOR_READERS.has(text)) return text;

  for (const [pattern, reword] of REWORDED) {
    const match = pattern.exec(text);
    if (match) return reword(match);
  }

  return fallback;
};
